import { createContext, useContext, useEffect, useRef, useState, ReactNode } from 'react'
import { Observable, Subscription } from 'rxjs'

import { SharedSocketService } from '../services/socket'
import {
    getCurrentUserInfo,
    getSocketConfig,
    resolveChannel
} from '../services/mobilePaymentBarApi'
import { logger } from '../utils/logger'
import { authService } from '../services/auth'

interface SocketContextValue {
    service: SharedSocketService | null
    isConnected: boolean
    subscribe: <T>(event: string, handler: (payload: T) => void) => Subscription | null
}

const SocketContext = createContext<SocketContextValue | null>(null)

interface SocketProviderProps {
    children: ReactNode
}

export const SocketProvider = ({ children }: SocketProviderProps) => {
    const [service, setService] = useState<SharedSocketService | null>(null)
    const [isConnected, setIsConnected] = useState(false)
    const serviceRef = useRef<SharedSocketService | null>(null)
    const connectionSubscriptionRef = useRef<Subscription | null>(null)
    const reconnectTimeoutRef = useRef<number | undefined>(undefined)
    const isInitializingRef = useRef(false)

    useEffect(() => {
        if (typeof window === 'undefined' || isInitializingRef.current) {
            return
        }

        isInitializingRef.current = true

        const initializeSocket = async () => {
            try {
                // Wait for authentication to complete before initializing socket
                await authService.waitForLogin()
                
                logger.log('SOCKET', 'Authentication complete, initializing socket...')
                
                const [currentUser, socketConfig] = await Promise.all([
                    getCurrentUserInfo(),
                    getSocketConfig()
                ])

                logger.log('SOCKET', 'Socket initialization data', {
                    hasUser: !!currentUser,
                    userId: currentUser.id,
                    walletId: currentUser.walletId,
                    hasConfig: !!socketConfig,
                })

                const channel = resolveChannel(socketConfig, currentUser)
                if (!channel) {
                    logger.warn('SOCKET', 'Unable to resolve channel for socket', {
                        hasUser: !!currentUser,
                        userId: currentUser?.id,
                        walletId: currentUser?.walletId,
                    })
                    isInitializingRef.current = false
                    return
                }

                logger.log('SOCKET', 'Resolved channel', { channel })

                const identifier = currentUser.walletId ?? currentUser.id
                const queryParams: Record<string, string> = {
                    userId: String(identifier),
                    channel
                }

                const socketService = new SharedSocketService({
                    config: {
                        ...socketConfig,
                        enableLogging: socketConfig.enableLogging ?? true
                    },
                    queryParams
                })

                serviceRef.current = socketService
                setService(socketService)

                const connectionSubscription = socketService.isConnected$.subscribe(connected => {
                    setIsConnected(connected)

                    if (!connected && reconnectTimeoutRef.current === undefined) {
                        reconnectTimeoutRef.current = window.setTimeout(() => {
                            reconnectTimeoutRef.current = undefined
                            if (serviceRef.current && !serviceRef.current.isConnected) {
                                serviceRef.current.connect()
                            }
                        }, 5000)
                    } else if (connected && reconnectTimeoutRef.current !== undefined) {
                        window.clearTimeout(reconnectTimeoutRef.current)
                        reconnectTimeoutRef.current = undefined
                    }
                })

                connectionSubscriptionRef.current = connectionSubscription

                socketService.connect()
                isInitializingRef.current = false
            } catch (error) {
                logger.warn('SOCKET', 'Unable to initialize socket', error)
                isInitializingRef.current = false

                reconnectTimeoutRef.current = window.setTimeout(() => {
                    reconnectTimeoutRef.current = undefined
                    void initializeSocket()
                }, 10000)
            }
        }

        void initializeSocket()

        return () => {
            if (reconnectTimeoutRef.current) {
                window.clearTimeout(reconnectTimeoutRef.current)
                reconnectTimeoutRef.current = undefined
            }
            if (connectionSubscriptionRef.current) {
                connectionSubscriptionRef.current.unsubscribe()
                connectionSubscriptionRef.current = null
            }
            if (serviceRef.current) {
                serviceRef.current.destroy()
                serviceRef.current = null
            }
            setService(null)
            setIsConnected(false)
        }
    }, [])

    const subscribe = <T,>(event: string, handler: (payload: T) => void): Subscription | null => {
        if (!service) {
            logger.warn('SOCKET', 'No service available')
            return null
        }

        logger.log('SOCKET', `Subscribing to event "${event}"`)
        const subscription = (service.on$(event) as Observable<T>).subscribe({
            next: payload => {
                logger.log('SOCKET', `Received event "${event}"`, payload)
                handler(payload)
            },
            error: error => {
                logger.warn('SOCKET', `Error in subscription for event "${event}"`, error)
            }
        })

        return subscription
    }

    return (
        <SocketContext.Provider value={{ service, isConnected, subscribe }}>
            {children}
        </SocketContext.Provider>
    )
}

export const useSocket = (): SocketContextValue => {
    const context = useContext(SocketContext)
    if (!context) {
        throw new Error('useSocket must be used within a SocketProvider')
    }
    return context
}

